import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { pairwise, startWith } from "rxjs";
import { PendoAgent } from "./pendo-agent";
import { PendoProfile } from "./profile/pendo-profile.model";
import { PendoProfileService } from "./profile/pendo-profile.service";

/**
 * Keeps Pendo identified as whoever the current profile belongs to: a new
 * visitor ends the previous one's session in Pendo, and each change to the
 * same visitor's profile updates it.
 */
@Injectable()
export class PendoSessionService {
    private readonly _agent = inject(PendoAgent);
    private readonly _profileService = inject(PendoProfileService);

    constructor() {
        this._initializeTracking();
    }

    private _initializeTracking(): void {
        this._profileService.profile$
            .pipe(
                startWith(null),
                pairwise(),
                takeUntilDestroyed(inject(DestroyRef)),
            )
            .subscribe(([previous, current]) =>
                this._updatePendo(previous, current),
            );
    }

    private _updatePendo(
        previous: PendoProfile | null,
        current: PendoProfile | null,
    ): void {
        if (!current) {
            this._agent.clear();
        } else if (previous && PendoProfile.hasSameIds(previous, current)) {
            this._agent.update(current);
        } else {
            this._agent.identify(current);
        }
    }
}
